import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { SVGIcon } from "@pearsontechnology/aero-components-v2";
import { NODE_TYPE_KEY_TERM } from "./constants";
import { KeyTermWrapper } from "./styles";
import { withInlineVoidElement } from "../../utils";
import {
  usePopover,
  getTextFromNodes,
  capitalize,
  PopoverWrapper,
  PopoverTitle,
  CloseButton,
  PopoverContent,
  PopoverStageContent,
  PopoverBreakOpportunity,
} from "../common";

export default function KeyTerm() {
  const plugin = (editor: any) => {
    withInlineVoidElement(editor, NODE_TYPE_KEY_TERM);
    return editor;
  };

  plugin.renderElement = ({ element, attributes, children }: any) => {
    if (element.type !== NODE_TYPE_KEY_TERM) {
      return undefined;
    }

    // Extract footnote marker text from node.nodes (the children)
    const termText = getTextFromNodes(element.children || []);
    // Only capitalize if the term is empty or starts with lowercase
    // This preserves original casing if it exists
    const term = termText.trim()
      ? termText.trim()[0] === termText.trim()[0].toUpperCase()
        ? termText.trim()
        : capitalize(termText.trim())
      : "";

    // Extract stage from nested data structure
    const data = element.data;
    const stage = data?.stage;

    // Get footnote ID from ref, dataId, or generate from node key
    const ref = data?.ref || stage?.ref;
    const dataId = data?.dataId;
    const termId = ref || dataId;

    return (
      <KeyTermComponent {...attributes} term={term} stage={stage} termId={termId} dataKey={dataId}>
        {children}
      </KeyTermComponent>
    );
  };

  return plugin;
}

interface KeyTermComponentProps {
  term: string;
  stage: any;
  termId: string;
  dataKey?: string | number;
  children: React.ReactNode;
  [key: string]: any;
}

const KeyTermComponent: React.FC<KeyTermComponentProps> = ({
  term,
  stage,
  termId,
  dataKey,
  children,
  ...attributes
}) => {
  const {
    showPopover,
    hasYOverflow,
    arrowXOffset,
    wrapperRef,
    popoverRef,
    closeButtonRef,
    handleClick,
    handleClose,
    handleKeyDown,
  } = usePopover();

  const [displayTerm, setDisplayTerm] = useState(term || "Key Term");

  // Update display term from the rendered element text content
  useEffect(() => {
    if (term && term.trim()) {
      setDisplayTerm(term);
      return;
    }

    // Try to get text from the wrapper element after it's rendered
    const updateTermFromElement = () => {
      if (wrapperRef.current) {
        const text = wrapperRef.current.textContent || wrapperRef.current.innerText;
        if (text && text.trim()) {
          setDisplayTerm(capitalize(text.trim()));
          return;
        }
      }
    };

    // Try immediately
    updateTermFromElement();

    // Also try after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(updateTermFromElement, 0);

    return () => clearTimeout(timeoutId);
  }, [term, children, wrapperRef]);

  return (
    <>
      <KeyTermWrapper
        {...attributes}
        ref={wrapperRef as React.RefObject<HTMLAnchorElement>}
        href="#"
        data-key-term-id={termId}
        onClick={handleClick}
        role="button"
        aria-expanded={showPopover}
        aria-haspopup="dialog"
        onKeyDown={handleKeyDown}
      >
        <span>{children}</span>
      </KeyTermWrapper>
      <PopoverBreakOpportunity aria-hidden="true" />
      {showPopover &&
        createPortal(
          <PopoverWrapper
            ref={popoverRef}
            id={termId}
            {...({ type: "key-term", "data-key": String(dataKey) } as any)}
            className="popover-wrapper key-term"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${termId}-title`}
            tabIndex={-1}
            isOpen={showPopover}
            hasYOverflow={hasYOverflow}
            arrowXOffset={arrowXOffset}
          >
            <CloseButton
              onClick={handleClose}
              aria-label="Close"
              className="popover-close-btn"
              type="button"
              tabIndex={0}
              ref={closeButtonRef}
            >
              <SVGIcon icon="closeX" />
            </CloseButton>
            <PopoverTitle id={`${termId}-title`}>{displayTerm}</PopoverTitle>
            <PopoverContent>
              {stage ? <PopoverStageContent stage={stage} /> : <div>Content not available</div>}
            </PopoverContent>
          </PopoverWrapper>,
          document.getElementById("viewer-express-container") ?? document.body,
        )}
    </>
  );
};
